import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { BrandWordmark, BrandMark } from "@/components/BrandMark";
import { ShieldCheck, ArrowLeft, AlertTriangle } from "lucide-react";

export const VerifyCertificate = () => {
  const { ref } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cert", ref],
    queryFn: async () => (await api.get(`/certificates/${ref}`)).data,
    retry: false,
  });

  return (
    <div className="min-h-screen p-6 lg:p-12">
      <div className="flex items-center justify-between mb-8">
        <BrandMark size={140} />
        <Link to="/portal" className="cad-btn-outline px-3 py-1.5 rounded-sm text-[12px] inline-flex items-center gap-1.5"><ArrowLeft size={12}/> Portal</Link>
      </div>

      {isLoading && <div className="text-[#B3C2CC]">Verifying reference…</div>}

      {isError && (
        <div className="cad-panel p-8 max-w-xl mx-auto text-center" data-testid="verify-not-found">
          <AlertTriangle className="mx-auto text-[#E67E7E]" size={32} />
          <div className="font-display text-xl font-bold text-[#F6F8FA] mt-3">Certificate not found</div>
          <div className="text-[13px] text-[#B3C2CC] mt-2">
            The reference <span className="font-mono-cad text-[#F6F8FA]">{ref}</span> was not located in the Cadastre360™
            demonstration register.
          </div>
        </div>
      )}

      {data && (
        <div className="max-w-3xl mx-auto cad-panel p-8 relative" data-testid="verify-certificate">
          <div className="absolute inset-0 cad-topo opacity-30 pointer-events-none"></div>
          <div className="relative flex items-start justify-between">
            <div>
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
                {data.kind === "mineral_right_certificate" ? "Mineral Right Certificate" : "Parcel Extract"}
              </div>
              <div className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">{data.reference}</div>
              <div className="text-[13px] text-[#B3C2CC] mt-1">Issued by {data.issuer}</div>
            </div>
            <BrandMark size={148} responsive={false} />
          </div>

          <div className="relative mt-6 grid grid-cols-2 gap-3 text-[13px]">
            {Object.entries(data)
              .filter(([k]) => !["kind","reference","note","verified","verified_at","issuer"].includes(k))
              .map(([k, v]) => (
                <div key={k} className="border-b cad-hairline pb-2">
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{k.replace(/_/g, " ")}</div>
                  <div className="text-[#F6F8FA] mt-0.5 font-mono-cad">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v ?? "—")}</div>
                </div>
              ))}
          </div>

          <div className="relative mt-6 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="cad-chip cad-chip-green"><ShieldCheck size={12}/> Verified {data.verified_at?.slice(0, 19).replace("T"," ")}</div>
              <div className="text-[11px] text-[#7C93A5] mt-3 font-mono-cad uppercase tracking-[0.18em]">{data.note}</div>
            </div>
            <img
              src={`${API}/certificates/${data.reference}/qr.png`}
              alt="Certificate QR"
              className="w-28 h-28 rounded-sm border cad-hairline bg-[#F6F8FA]"
              data-testid="verify-qr"
            />
          </div>
        </div>
      )}
    </div>
  );
};
